// =============================================================================
// shareRelease.ts — HOP Slice 3, Stage B.
// Send a "progress update" (batch of curated photos) into a project's client
// portal, straight from the pack page. Two guarantees:
//   1. One persistent link per project — reused if it exists, minted once if not.
//   2. Every shared photo carries a SNAPSHOT of its category / instance /
//      subcategory label (migration 190). The portal reads these verbatim, so
//      grouping never depends on a live re-resolve and S3 vs K3 stay distinct.
// =============================================================================

import { supabase } from "@/lib/supabase";

const EXPIRY_DAYS = 20;

function mintToken(): string {
  return (crypto.randomUUID() + crypto.randomUUID()).replace(/-/g, "").slice(0, 40);
}

/** Reuse the project's live client link, or mint one. Extends expiry on use. */
export async function getOrCreateShare(
  projectId: string,
  userId: string | null,
  defaultTitle: string,
): Promise<{ share_id: string; token: string }> {
  const expires = new Date(Date.now() + EXPIRY_DAYS * 86400000).toISOString();
  const { data: existing } = await supabase
    .from("hop_shares")
    .select("share_id, token, revoked_at, created_at")
    .eq("project_id", projectId)
    .order("created_at", { ascending: false })
    .limit(5);
  const live = (existing ?? []).find((s: any) => !s.revoked_at);
  if (live) {
    await supabase.from("hop_shares").update({ expires_at: expires }).eq("share_id", (live as any).share_id);
    return { share_id: (live as any).share_id, token: (live as any).token };
  }
  const token = mintToken();
  const { data: sh, error } = await supabase
    .from("hop_shares")
    .insert({ project_id: projectId, token, title: defaultTitle, created_by: userId, expires_at: expires })
    .select("share_id, token")
    .single();
  if (error || !sh) throw new Error(error?.message ?? "Couldn't create the client portal (permission?).");
  return { share_id: (sh as any).share_id, token: (sh as any).token };
}

/**
 * Every asset_id already shared on the LIVE portal. T-419 (James's reset):
 * revoked shares and retracted batches no longer count — revoking the link
 * (or retracting an update) frees those photos for re-sharing on the fresh
 * link, instead of leaving them stuck "✓ shared" against a dead portal.
 */
export async function fetchAlreadyShared(projectId: string): Promise<Set<string>> {
  const { data: shares } = await supabase.from("hop_shares")
    .select("share_id")
    .eq("project_id", projectId)
    .is("revoked_at", null);
  const shareIds = (shares ?? []).map((s: any) => s.share_id);
  if (!shareIds.length) return new Set();
  const { data: batches } = await supabase.from("hop_share_batches")
    .select("batch_id")
    .in("share_id", shareIds)
    .is("revoked_at", null);
  const batchIds = (batches ?? []).map((b: any) => b.batch_id);
  if (!batchIds.length) return new Set();
  const { data: photos } = await supabase.from("hop_share_photos").select("asset_id").in("batch_id", batchIds);
  return new Set((photos ?? []).map((p: any) => p.asset_id));
}

/**
 * Create a progress-update batch and insert the picked photos as pointers,
 * each stamped with its category/instance/subcat label. Handles selections
 * that span instances or categories (labels resolved from the DB, not the
 * currently-loaded page).
 */
export async function sendProgressUpdate(opts: {
  projectId: string;
  userId: string | null;
  assetIds: string[];
  title?: string;
  note?: string;
  defaultTitle: string;
}): Promise<{ token: string; count: number }> {
  const { projectId, userId, assetIds, title, note, defaultTitle } = opts;
  if (assetIds.length === 0) throw new Error("Pick at least one photo to send.");

  const { share_id, token } = await getOrCreateShare(projectId, userId, defaultTitle);

  const { data: batch, error: bErr } = await supabase
    .from("hop_share_batches")
    .insert({ share_id, title: title?.trim() || null, note: note?.trim() || null, created_by: userId })
    .select("batch_id")
    .single();
  if (bErr || !batch) throw new Error(bErr?.message ?? "Couldn't create the update.");
  const batch_id = (batch as any).batch_id;

  // Resolve the label snapshot for every selected asset (may span instances).
  const { data: assets } = await supabase
    .from("hop_assets")
    .select("asset_id, pack_category_id, instance_id, pack_subcategory_id")
    .in("asset_id", assetIds);
  const arr = (assets ?? []) as any[];
  const catIds  = [...new Set(arr.map(a => a.pack_category_id).filter(Boolean))];
  const instIds = [...new Set(arr.map(a => a.instance_id).filter(Boolean))];
  const subIds  = [...new Set(arr.map(a => a.pack_subcategory_id).filter(Boolean))];
  const [catRes, instRes, subRes] = await Promise.all([
    catIds.length  ? supabase.from("hop_pack_categories").select("pack_category_id, display_name").in("pack_category_id", catIds) : Promise.resolve({ data: [] as any[] }),
    instIds.length ? supabase.from("hop_pack_category_instances").select("instance_id, instance_code").in("instance_id", instIds) : Promise.resolve({ data: [] as any[] }),
    subIds.length  ? supabase.from("hop_pack_subcategories").select("pack_subcategory_id, display_name").in("pack_subcategory_id", subIds) : Promise.resolve({ data: [] as any[] }),
  ]);
  const catMap  = new Map(((catRes.data  ?? []) as any[]).map(r => [r.pack_category_id, r.display_name]));
  const instMap = new Map(((instRes.data ?? []) as any[]).map(r => [r.instance_id, r.instance_code]));
  const subMap  = new Map(((subRes.data  ?? []) as any[]).map(r => [r.pack_subcategory_id, r.display_name]));

  const rows = arr.map(a => ({
    batch_id,
    asset_id: a.asset_id,
    category_label: catMap.get(a.pack_category_id) ?? null,
    instance_label: instMap.get(a.instance_id) ?? null,
    subcat_label:   subMap.get(a.pack_subcategory_id) ?? null,
  }));
  const { error: pErr } = await supabase.from("hop_share_photos").insert(rows);
  if (pErr) throw new Error(pErr.message);

  return { token, count: rows.length };
}

/**
 * Share acceptance documents (deliverable PDFs etc.) into the portal as their
 * own dated batch. Documents are project-level, so this runs from the portal
 * management modal rather than the per-category pack page.
 */
export async function sendDocuments(opts: {
  projectId: string;
  userId: string | null;
  uploadIds: string[];
  title?: string;
  defaultTitle: string;
}): Promise<{ token: string; count: number }> {
  const { projectId, userId, uploadIds, title, defaultTitle } = opts;
  if (uploadIds.length === 0) throw new Error("Pick at least one document to send.");
  const { share_id, token } = await getOrCreateShare(projectId, userId, defaultTitle);
  const { data: batch, error: bErr } = await supabase
    .from("hop_share_batches")
    .insert({ share_id, title: title?.trim() || "Documents", note: null, created_by: userId })
    .select("batch_id")
    .single();
  if (bErr || !batch) throw new Error(bErr?.message ?? "Couldn't create the update.");
  const { error: dErr } = await supabase.from("hop_share_docs")
    .insert(uploadIds.map((upload_id) => ({ batch_id: (batch as any).batch_id, upload_id })));
  if (dErr) throw new Error(dErr.message);
  return { token, count: uploadIds.length };
}
